import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { shuffle } from "lodash";

import { filesURL } from "config";
import { getThemeImages } from "actions/flinkPlayActions";
import { isMobileAppRuntime } from "utils";
import {
  listOfflineAssetKeysSync,
  logOfflineDiagnosticEvent,
  resolveOfflineAssetKeySync
} from "mobile-modules/offline/assets";
import { isOnline } from "mobile-modules/offline/state";

const idOf = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const themeNameFor = (solution, themeValue) => {
  if (themeValue && typeof themeValue === 'object' && themeValue.name) {
    return String(themeValue.name);
  }
  const themeId = idOf(themeValue);
  const themes = Array.isArray(solution?.themes) ? solution.themes : [];
  const match = themes.find((theme) => idOf(theme) === themeId);
  return String(match?.name || '');
};

const localThemeImages = (solution, themeValue) => {
  const name = themeNameFor(solution, themeValue);
  if (!name) return [];
  const folder = name.split(' ').join('');
  return listOfflineAssetKeysSync(`Themes/${folder}/Backgrounds/`)
    .filter((key) => /\.[^/]+$/i.test(key));
};

const ThemeBackground = ({
  solution,
  settings,
  getThemeImages,
  currentActivity
}) => {
  const [images, setImages] = useState();
  const [currentIndex, setIndex] = useState(-1);

  useEffect(() => {
    setIndex(currentIndex + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActivity]);

  const configuredTheme =
    settings &&
    solution &&
    settings.bySolution &&
    settings.bySolution[solution._id] &&
    settings.bySolution[solution._id].theme;

  const defaultTheme = solution && solution.defaultTheme;
  const defaultThemeId =
    defaultTheme && typeof defaultTheme === 'object'
      ? (defaultTheme._id || defaultTheme.id || '')
      : (defaultTheme || '');

  // Desktop keeps the legacy configured-theme behavior. Native mobile may use
  // the authored default theme when the learner has not stored a theme yet.
  const selectedTheme = configuredTheme || (isMobileAppRuntime() ? defaultTheme : '');
  const selectedThemeId = idOf(selectedTheme) || defaultThemeId;

  useEffect(() => {
    let active = true;
    if (!selectedThemeId) {
      setImages([]);
      return undefined;
    }

    // Native mobile has two explicit runtime modes:
    // ONLINE  -> load theme backgrounds from the server, exactly like web.
    // OFFLINE -> use only the already-downloaded local theme backgrounds.
    if (isMobileAppRuntime() && !isOnline()) {
      const localImages = localThemeImages(solution, selectedTheme);
      if (localImages.length) {
        setImages(shuffle(localImages));
        return undefined;
      }

      setImages([]);
      logOfflineDiagnosticEvent('theme-background-list-missing', {
        themeId: selectedThemeId,
        themeName: themeNameFor(solution, selectedTheme),
        scope: 'custom-level',
      });
      return undefined;
    }

    // Web and ONLINE native mobile always use the server-provided image list.
    getThemeImages(selectedThemeId).then((themeImages = []) => {
      if (!active) return;
      const safeImages = Array.isArray(themeImages) ? themeImages : [];
      setImages(shuffle(safeImages.map((item) => item && item.key).filter(Boolean)));
    });

    return () => {
      active = false;
    };
  }, [selectedThemeId, selectedTheme, solution, getThemeImages]);

  const style = useMemo(() => {
    const regexp = /\./i;
    let imgLink = '';
    let filterImg = [];

    if (images && images.length) {
      filterImg = images.filter(item => {
        const imgLinkArr = item.split("/");
        if (!imgLinkArr[imgLinkArr.length - 1].match(regexp)) return false;
        return true;
      });

      imgLink = filterImg.length
        ? (filterImg[currentIndex % filterImg.length] || filterImg[0])
        : '';
    }

    const useLocalBackground = isMobileAppRuntime() && !isOnline();
    const backgroundSrc = imgLink
      ? (useLocalBackground
        ? resolveOfflineAssetKeySync(imgLink)
        : `${filesURL}/${imgLink}`)
      : '';

    return {
      backgroundImage: backgroundSrc ? `url(${backgroundSrc})` : 'none',
      backgroundPosition: "center",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundColor: isMobileAppRuntime() ? '#f4f7fb' : undefined,
      position: "fixed",
      zIndex: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    };
  }, [images, currentIndex]);

  return <div style={style} />;
};

ThemeBackground.propTypes = {
  solution: PropTypes.object,
  settings: PropTypes.object,
  getThemeImages: PropTypes.func.isRequired
};

const mapStateToProps = ({ flinkPlay, game }) => ({
  currentActivity: game.currentActivity,
  solution: flinkPlay.solution,
  settings: flinkPlay.learner?.settings || {}
});

export default connect(mapStateToProps, { getThemeImages })(ThemeBackground);
